import React, { useEffect, useRef, useState } from "react"
import { View, Pressable } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { SvgXml } from "react-native-svg"
import { connect } from "react-redux"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { ModalM } from "@common/components/modal/ModalM"
import SvgViewer from "./SvgViewer"

const MaterialSvgDisplay = (props) => {
    const { material, style = {}, showBackgroundGenerationMessage = false, height = 400 } = props
    const materialId = material?.id
    const [svgContent, setSvgContent] = useState(material?.figure_svg)
    const [isPolling, setIsPolling] = useState(false)
    const [hasTimedOut, setHasTimedOut] = useState(false)
    const [isViewerOpen, setIsViewerOpen] = useState(false)
    const intervalRef = useRef()
    const timeoutRef = useRef()

    // Handle API response
    useApiStatus(
        activitiesService.getMaterial,
        null,
        false,
        (data) => {
            const material = data
            if (material?.figure_svg && material?.id === materialId) {
                setSvgContent(material.figure_svg)
                setIsPolling(false)
                clearInterval(intervalRef.current)
                clearTimeout(timeoutRef.current)
            }
        },
        () => {
            // On error, continue polling
        },
        true // noIdle - don't clear the API state
    )

    // Start polling when component mounts
    useEffect(() => {
        if (!materialId) return

        setIsPolling(true)
        setHasTimedOut(false)

        // Initial request
        if (!svgContent) {
            props.apiRequest(activitiesService.getMaterial, { id: materialId })
        }

        // Set up polling interval (every 5 seconds)
        intervalRef.current = setInterval(() => {
            if (!hasTimedOut && !svgContent) {
                props.apiRequest(activitiesService.getMaterial, { id: materialId })
            }
        }, 5000)

        // Set up timeout (90 seconds)
        timeoutRef.current = setTimeout(() => {
            setIsPolling(false)
            setHasTimedOut(true)
            clearInterval(intervalRef.current)
        }, 90000)

        // Cleanup on unmount
        return () => {
            clearInterval(intervalRef.current)
            clearTimeout(timeoutRef.current)
        }
    }, [materialId])

    // Cleanup when SVG is loaded or timeout occurs
    useEffect(() => {
        if (svgContent || hasTimedOut) {
            clearInterval(intervalRef.current)
            clearTimeout(timeoutRef.current)
        }
    }, [svgContent, hasTimedOut])

    // Don't render anything if no material ID
    if (!materialId) {
        return null
    }

    // Show SVG if available
    if (svgContent) {
        return (
            <>
                <Pressable
                    onPress={() => setIsViewerOpen(true)}
                    style={[
                        {
                            marginHorizontal: 10,
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 8,
                            paddingVertical: 10,
                        },
                        style,
                    ]}
                >
                    <SvgXml
                        xml={svgContent}
                        width="90%"
                        height={height}
                    />
                </Pressable>

                <ModalM
                    visible={isViewerOpen}
                    onDismiss={() => setIsViewerOpen(false)}
                    style={{
                        width: "96%",
                        maxHeight: "90%",
                        height: "90%",
                        flex: 1,
                        backgroundColor: theme.colors.black,
                    }}
                >
                    <SvgViewer
                        svgContent={svgContent}
                        close={() => setIsViewerOpen(false)}
                    />
                </ModalM>
            </>
        )
    }

    // Show subtle loading indicator while polling
    if (isPolling && !hasTimedOut) {
        return (
            <View
                style={[
                    {
                        alignItems: "center",
                        justifyContent: "center",
                        paddingVertical: 15,
                        backgroundColor: theme.colors.lightGrey || theme.colors.grey + "10",
                        borderRadius: 8,
                        margin: 10,
                    },
                    style,
                ]}
            >
                <ActivityIndicator 
                    color={theme.colors.primary} 
                    size="small" 
                    style={{ opacity: 0.7 }}
                />
                <TextM
                    style={{ marginTop: 6, textAlign: "center" }}
                    color={theme.colors.grey}
                    fontSize={11}
                >
                    Génération de la figure...
                </TextM>
                {showBackgroundGenerationMessage && (
                    <TextM
                        style={{ marginTop: 4, textAlign: "center", paddingHorizontal: 10 }}
                        color={theme.colors.grey}
                        fontSize={8}
                        wrap
                    >
                        Tu peux créer l&apos;activité, la génération de l&apos;image continuera en arrière-plan
                    </TextM>
                )}
            </View>
        )
    }

    // Show subtle timeout message
    if (hasTimedOut) {
        return (
            <View
                style={[
                    {
                        alignItems: "center",
                        justifyContent: "center",
                        paddingVertical: 8,
                        backgroundColor: theme.colors.lightGrey || theme.colors.grey + "05",
                        borderRadius: 6,
                        marginVertical: 10,
                    },
                    style,
                ]}
            >
                <TextM
                    style={{ textAlign: "center" }}
                    color={theme.colors.grey}
                    fontSize={10}
                >
                    Schéma non disponible
                </TextM>
            </View>
        )
    }

    // Default: don't show anything
    return null
}

const mapStateToProps = (state) => {
    return {
        getMaterialRequest: state.api?.getMaterial?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(MaterialSvgDisplay)
